import { idOrIdentifierSchema } from "@suse-o11y/id-or-identifier";
import { prop } from "@suse-o11y/prop";
import { nanoid } from "nanoid";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Switch } from "react-router-dom";

import { Permission } from "~/models/dto";

import { selectPlayGroundModal } from "../app/state/pages/WelcomeSlice";
import { usageTrack } from "../app/state/usage-track/UsageTrackActions";
import { useDropDetachedFiltersOnFirstLoad } from "../app/state/view/viewpoint/ViewPointHooks";
import { usePermissions } from "../common/use-permissions";
import { BrowserPageTitle } from "../components/browser-page-title";
import { TrialExpiration } from "../components/react/license/trial/TrialExpiration";
import { generatePath } from "../generic/routing";
import { queryUIConfig, selectEnableAi } from "../queries";
import { useQueryMainMenuGroups } from "../queries/mainmenu";
import { useQuery } from "../queries/react-query";

import { useDashboardsPermissions } from "./dashboards/permissions";
import { PlaygroundPopup } from "./welcome-page/PlaygroundPopup";
import { LazyRoute } from "./LazyRoute";
import {
	ANALYTICS_PATH,
	ASSUME_ROLE_PATH,
	CLIPROFILE_PATH,
	COMPONENT_PATH,
	DASHBOARDS_OVERVIEW_PATH,
	DASHBOARD_PATH,
	KUBERNETES_OVERVIEW_PATH,
	METRICS_PATH,
	MONITORS_PATH,
	NOTIFICATIONS_PATH,
	SESSIONS_PATH,
	SETTINGS_PATH,
	STACKPACKS_PATH,
	STACKPACKS_STACKPACK_PATH,
	TRACES_PATH,
	VIEWS_OVERVIEW_PATH,
	VIEW_PATH,
	WELCOME_PATH,
} from "./Routes";

const LazyWelcomePage = React.lazy(() =>
	import("./welcome-page").then(({ WelcomePage }) => ({ default: WelcomePage })),
);
const LazyAssumeRole = React.lazy(() =>
	import("./assume/AssumeRole").then(({ AssumeRole }) => ({ default: AssumeRole })),
);
const LazySettingsRootRoute = React.lazy(() =>
	import("./settings/SettingsRootRoute").then(({ SettingsRootRoute }) => ({ default: SettingsRootRoute })),
);
const LazyView = React.lazy(() => import("../bottom_content/view/panes/View").then(({ View }) => ({ default: View })));
const LazyCLIProfile = React.lazy(() =>
	import("../bottom_content/cliprofile/CLIProfile").then(({ CLIProfile }) => ({ default: CLIProfile })),
);
const LazyStackPacksRootRoute = React.lazy(() =>
	import("./stackPacks/StackPacksRootRoute").then(({ StackPacksRootRoute }) => ({
		default: StackPacksRootRoute,
	})),
);
const LazyAnalytics = React.lazy(() =>
	import("../bottom_content/analytics/Analytics").then(({ Analytics }) => ({ default: Analytics })),
);
const LazyViewsOverview = React.lazy(() =>
	import("../bottom_content/overview/Overview").then(({ Overview }) => ({ default: Overview })),
);

const LazyDashboardsOverview = React.lazy(() =>
	import("./dashboards/_overview").then(({ Overview: DashboardsOverview }) => ({
		default: DashboardsOverview,
	})),
);

const LazyDashboard = React.lazy(() =>
	import("./dashboards/dashboard").then(({ Dashboard }) => ({ default: Dashboard })),
);

const LazyMetrics = React.lazy(() => import("../metrics/Metrics").then(({ Metrics }) => ({ default: Metrics })));

const LazyTraces = React.lazy(() => import("./_traces").then(({ Traces }) => ({ default: Traces })));

const LazyMonitorsRootRoute = React.lazy(() =>
	import("../monitors/MonitorsRootRoute").then(({ MonitorsRootRoute }) => ({
		default: MonitorsRootRoute,
	})),
);
const LazyNotifications = React.lazy(() =>
	import("./Notifications").then(({ Notifications }) => ({ default: Notifications })),
);

const LazySessions = React.lazy(() =>
	import("../sessions/sessions-content").then(({ Sessions }) => ({ default: Sessions })),
);

const LazyKubernetesOverview = React.lazy(() =>
	import("../_kubernetes-overview").then(({ KubernetesOverview }) => ({ default: KubernetesOverview })),
);

const ANONYMOUS_USER_ID_LOCAL_STORAGE_KEY = "__sts_user_id__";

const DEFAULT_STACKPACK = "kubernetes-v2";

export const StsRouter: React.FC = () => {
	const dispatch = useDispatch();
	const permissions = usePermissions();
	const playgroundModalOpen = useSelector(selectPlayGroundModal);
	const { data: uiConfig } = useQuery(queryUIConfig);
	const enableAi = uiConfig ? selectEnableAi(uiConfig) : false;
	const hasExecuteAiPermission = permissions?.hasSystemPermission(Permission.ExecuteAi);
	const hasGetDashboardsPermission = useDashboardsPermissions()
		.transform(prop("hasGetDashboardsPermission"))
		.getOrElse(undefined);

	const { data: mainMenuGroups } = useQueryMainMenuGroups();
	const firstMenuItem = mainMenuGroups?.flatMap(({ items }) => items).at(0);
	const defaultPagePath = firstMenuItem
		? generatePath(VIEW_PATH, { slug: firstMenuItem.identifier })
		: mainMenuGroups == null
			? undefined
			: generatePath(STACKPACKS_STACKPACK_PATH, { name: DEFAULT_STACKPACK });

	React.useEffect(() => {
		const anonymousUserId = localStorage.getItem(ANONYMOUS_USER_ID_LOCAL_STORAGE_KEY) ?? nanoid();

		localStorage.setItem(ANONYMOUS_USER_ID_LOCAL_STORAGE_KEY, anonymousUserId);

		dispatch(
			usageTrack({
				name: "ANONYMOUS_USER_ID",
				value: anonymousUserId,
			}),
		);
	}, [dispatch]);

	// When a URL is copy/pasted to a new browser window this hook will take care that
	// only the currently supported detached filters are kept and all others are ignored
	// NOTE: This only works on first load, so copy/pasting a URL into an existing StackState window will include
	// all detached filters :(
	useDropDetachedFiltersOnFirstLoad();

	return (
		<>
			<Switch>
				{uiConfig?.webUIConfig.featureFlags.showWelcomePage !== false && (
					<LazyRoute path={WELCOME_PATH}>
						<BrowserPageTitle fragment="Welcome">
							<LazyWelcomePage />
						</BrowserPageTitle>
					</LazyRoute>
				)}

				<LazyRoute path={ASSUME_ROLE_PATH}>
					<LazyAssumeRole />
				</LazyRoute>

				<LazyRoute
					isAllowed={
						permissions?.hasSystemPermission(Permission.GetSettings) &&
						permissions?.hasSystemPermission(Permission.UpdateSettings)
					}
					path={SETTINGS_PATH}
				>
					<BrowserPageTitle fragment="Settings">
						<LazySettingsRootRoute />
					</BrowserPageTitle>
				</LazyRoute>

				<LazyRoute
					exact
					path={VIEW_PATH}
					render={({ match: { params } }) => (
						<BrowserPageTitle fragment="Views">
							<LazyView identifier={params.slug} type="view" />
						</BrowserPageTitle>
					)}
				/>

				<LazyRoute
					exact
					path={COMPONENT_PATH}
					render={({ match: { params } }) => (
						<BrowserPageTitle fragment="Components">
							<LazyView identifier={params.identifier} type="component" />
						</BrowserPageTitle>
					)}
				/>

				<LazyRoute exact path={KUBERNETES_OVERVIEW_PATH}>
					<BrowserPageTitle fragment="Kubernetes">
						<LazyKubernetesOverview />
					</BrowserPageTitle>
				</LazyRoute>

				<LazyRoute exact isAllowed={permissions?.hasSystemPermission(Permission.GetApiTokens)} path={CLIPROFILE_PATH}>
					<BrowserPageTitle fragment="CLI">
						<LazyCLIProfile />
					</BrowserPageTitle>
				</LazyRoute>

				<LazyRoute isAllowed={permissions?.hasSystemPermission(Permission.GetStackpacks)} path={STACKPACKS_PATH}>
					<BrowserPageTitle fragment="StackPacks">
						<LazyStackPacksRootRoute />
					</BrowserPageTitle>
				</LazyRoute>

				<LazyRoute isAllowed={permissions?.hasSystemPermission(Permission.ExecuteScripts)} path={ANALYTICS_PATH}>
					<BrowserPageTitle fragment="Analytics">
						<LazyAnalytics />
					</BrowserPageTitle>
				</LazyRoute>

				<LazyRoute exact path={VIEWS_OVERVIEW_PATH}>
					<BrowserPageTitle fragment="Views">
						<LazyViewsOverview />
					</BrowserPageTitle>
				</LazyRoute>

				<LazyRoute path={METRICS_PATH}>
					<BrowserPageTitle fragment="Metrics">
						<LazyMetrics />
					</BrowserPageTitle>
				</LazyRoute>

				<LazyRoute exact isAllowed={hasGetDashboardsPermission} path={DASHBOARDS_OVERVIEW_PATH}>
					<BrowserPageTitle fragment="Dashboards">
						<LazyDashboardsOverview />
					</BrowserPageTitle>
				</LazyRoute>

				<LazyRoute
					exact
					isAllowed={hasGetDashboardsPermission}
					path={DASHBOARD_PATH}
					render={({ match: { params } }) => (
						<BrowserPageTitle fragment="Dashboards">
							<LazyDashboard
								// remount the component when the idOrIdentifier changes to reset its state
								idOrIdentifier={idOrIdentifierSchema.parse(params.idOrIdentifier)}
								key={params.idOrIdentifier}
							/>
						</BrowserPageTitle>
					)}
				/>

				<LazyRoute path={TRACES_PATH}>
					<BrowserPageTitle fragment="Traces">
						<LazyTraces />
					</BrowserPageTitle>
				</LazyRoute>

				<LazyRoute isAllowed={permissions?.hasSystemPermission(Permission.GetMonitors)} path={MONITORS_PATH}>
					<BrowserPageTitle fragment="Monitors">
						<LazyMonitorsRootRoute />
					</BrowserPageTitle>
				</LazyRoute>

				{enableAi && (
					<LazyRoute exact isAllowed={hasExecuteAiPermission} path={SESSIONS_PATH}>
						<BrowserPageTitle fragment="AI Sessions">
							<LazySessions />
						</BrowserPageTitle>
					</LazyRoute>
				)}

				<LazyRoute isAllowed={permissions?.hasSystemPermission(Permission.GetNotifications)} path={NOTIFICATIONS_PATH}>
					<BrowserPageTitle fragment="Notifications">
						<LazyNotifications />
					</BrowserPageTitle>
				</LazyRoute>

				{/* redirect to default page if user is in the root of stackstate */}
				<LazyRoute exact path="/">
					{uiConfig?.webUIConfig.featureFlags.showWelcomePage === false && defaultPagePath != null && (
						<Redirect to={defaultPagePath} />
					)}
					{uiConfig?.webUIConfig.featureFlags.showWelcomePage === true && <Redirect to={WELCOME_PATH} />}
				</LazyRoute>

				{/* in case of errors – redirect to the views overview page */}
				{uiConfig != null && <Redirect to={VIEWS_OVERVIEW_PATH} />}
			</Switch>

			<TrialExpiration />

			<PlaygroundPopup open={playgroundModalOpen} />
		</>
	);
};
